import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { UploadSimple } from "@phosphor-icons/react";
import { formatCompact, formatINR } from "@/lib/format";
import { KpiGrid } from "@/components/project/KpiGrid";

const axisFmt = (v) => formatCompact(v).replace("₹", "");

export function OverviewTab({ project, calc, onGoto }) {
  if (!calc.itemCount) {
    return (
      <div data-testid="overview-empty" className="border-2 border-dashed border-slate-300 rounded-sm bg-white p-12 text-center">
        <UploadSimple className="mx-auto text-slate-400" size={44} />
        <h3 className="font-heading text-xl font-bold mt-4">No BOQ items yet</h3>
        <p className="text-sm text-slate-500 mt-1">Upload the Government BOQ to see your project financials.</p>
        <button data-testid="overview-goto-upload" onClick={() => onGoto("upload")} className="mt-5 inline-flex items-center px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-sm hover:bg-emerald-700 transition-colors">Upload BOQ</button>
      </div>
    );
  }
  const data = calc.categories.slice(0, 10).map((c) => ({ name: c.category, Revenue: Math.round(c.revenue), Expenditure: Math.round(c.expenditure), Profit: Math.round(c.profit) }));
  return (
    <div className="space-y-6">
      <KpiGrid calc={calc} project={project} />
      <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-5">
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5" data-testid="overview-category-chart">
          <h3 className="font-heading text-lg font-bold text-slate-900">Revenue vs Expenditure by Category</h3>
          <p className="text-xs text-slate-500 mb-4">Revenue after deduction compared with your market cost</p>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ left: 0, right: 8, top: 8, bottom: 40 }}>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} angle={-30} textAnchor="end" interval={0} height={60} />
                <YAxis tickFormatter={axisFmt} tick={{ fontSize: 11, fill: "#64748B" }} width={70} />
                <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} verticalAlign="top" height={30} />
                <Bar dataKey="Revenue" fill="#0F172A" radius={[2, 2, 0, 0]} />
                <Bar dataKey="Expenditure" fill="#F59E0B" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5" data-testid="overview-financial-summary">
          <h3 className="font-heading text-lg font-bold text-slate-900">Financial Summary</h3>
          <dl className="mt-4 divide-y divide-slate-100 text-sm">
            {[
              ["Govt BOQ Amount", calc.govtAmount],
              [`Less: ${project.default_deduction}% Below Govt Rate`, -calc.deductionAmount],
              ["Revenue after % Below", calc.revenue, "font-semibold"],
              ["Less: Estimated Expenditure", -calc.expenditure],
              ["Net Profit / Loss", calc.profit, `font-bold ${calc.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`],
              [`GST @ ${calc.gstRate}%`, calc.gst],
              ["   CGST", calc.cgst, "text-slate-500"],
              ["   SGST", calc.sgst, "text-slate-500"],
              ["Bill Value incl. GST", calc.revenueWithGst, "font-semibold"],
            ].map(([k, v, cls = ""]) => (
              <div key={k} className="flex justify-between gap-4 py-2.5">
                <dt className={`text-slate-600 whitespace-pre ${cls.includes("text-slate-500") ? "text-slate-500" : ""}`}>{k}</dt>
                <dd className={`tabular text-right ${cls || "text-slate-800"}`}>{formatINR(v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}
